import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {openCheckoutForCreditPackPurchase} from '@Modals/checkout-modal/checkout-modal.library';
import {AiAgentMode} from '@/ai-chat/providers/ai-chat-provider.types';
import {AiCreditsPill} from '@Components/ai-credits-pill/ai-credits-pill';
import {CreditsPillSize, CreditsPillTheme} from '@Components/ai-credits-pill/ai-credits-pill.types';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Button, Size, Type} from '@Components/button';
import {openAiChatAuthenticateModal} from '@Libraries/ai-chat.library';
import {isUserPremium, isUserPremiumPlus} from '@Libraries/user.library';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Text, TextSize} from '@Components/text';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import type {OverlayChildren} from 'react-bootstrap/Overlay';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {useAppSelector} from '@/hooks';
import {AiChatSuperUserOptions} from '@Components/ai-chat-super-user-options/ai-chat-super-user-options';
import styles from './ai-chat-account-status-bar.module.scss';
import {formatExpiryDate, getRenewalCreditsText} from './ai-chat-account-status-bar-library';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import {
  CREDITS_DROPDOWN_HOVER_CLOSE_DELAY_MS,
  MODAL_PANEL_PADDING_CLASS,
  MODAL_TEXT_CLASSES,
  TOOLTIP_TEXT_CLASSES,
  CREDITS_LEARN_MORE_URL,
  CREDITS_MODAL_PANEL_ID,
} from './ai-chat-account-status-bar.types';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import Divider from '@Components/divider';
import {CreditsModalContent} from '@Components/ai-chat-window/components/credits-modal-content/credits-modal-content';

export function AiChatAccountStatusBar(): ReactElement {
  const {state, actions} = useAiChat();
  const isPremiumUser = isUserPremium() || isUserPremiumPlus();
  const freeCredits = useAppSelector((s) => s.teamCredits.freeCredits);
  const freeCreditsExpiryDate = useAppSelector((s) => s.teamCredits.freeCreditsExpiryDate);
  const legacyCredits = useAppSelector((s) => s.teamCredits.legacyCredits);
  const legacyCreditsExpiryDate = useAppSelector((s) => s.teamCredits.legacyCreditsExpiryDate);
  const purchasedCredits = useAppSelector((s) => s.teamCredits.purchasedCredits);
  const renewalCredits = useAppSelector((s) => s.teamCredits.renewalCredits);
  const renewalDate = useAppSelector((s) => s.teamCredits.renewalDate);
  const [isCreditsDropdownOpen, setIsCreditsDropdownOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);

  const cancelScheduledClose = (): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = (): void => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => {
      setIsCreditsDropdownOpen(false);
    }, CREDITS_DROPDOWN_HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return (): void => {
      cancelScheduledClose();
    };
  }, []);

  const handleLoginClick = (): void => {
    void openAiChatAuthenticateModal(actions.onLoginSuccessActions);
  };

  const getLoginButton = (): ReactElement | null => {
    if (state.isUserLoggedIn) {
      return null;
    }

    return (
      <Button text={window.i18next.t('pmwjs_user_login_action')} icon={'icon-sign-in'} type={Type.GHOST} customClasses="radius-8" size={Size.SMALL} onClick={handleLoginClick} />
    );
  };

  const onAddCreditsClick = (): void => {
    setIsCreditsDropdownOpen(false);
    openCheckoutForCreditPackPurchase({
      headingText: '',
      modalTitle: window.i18next.t('pmwjs_select_a_credit_pack'),
      onSuccess: (): void => {
        void refreshTeamCreditsInStore();
        actions.refreshCredits();
      },
    });
  };

  const onUpgradeClick = (): void => {
    setIsCreditsDropdownOpen(false);
    showPurchasePremiumDialog();
  };

  const onLearnMoreClick = (): void => {
    setIsCreditsDropdownOpen(false);
    window.open(CREDITS_LEARN_MORE_URL, '_blank');
  };

  const getCreditsUsageNote = (): ReactElement | null => {
    if (state.mode !== AiAgentMode.AGENT) {
      return null;
    }

    return <Text val={window.i18next.t('pmwjs_ai_agent_credits_vary_by_task')} size={TextSize.XSMALL} className="body-md-bold ct-neutral-secondary _text-nowrap" />;
  };

  const getCreditsRow = (): ReactElement => {
    return (
      <div className={styles.creditsInfoRow}>
        <div className="flex-row-align-center gap-1">
          <Text val={window.i18next.t('pmwjs_ai_credits_remaining')} size={TextSize.XSMALL} className="ct-neutral-secondary body-md-regular" />
          {getExpiryBreakdownIcon()}
        </div>
        <div className="flex-row-align-end gap-1">
          <Icon icon="icon-flash" size={IconSize.SIZE_ICON_16} className={`ct-neutral-primary ${styles.creditIcon}`} />
          <div className="heading-h3 ct-neutral-primary">{state.credits.toLocaleString()}</div>
        </div>
        {getCreditsUsageNote()}
      </div>
    );
  };

  const getCreditsActionButton = (): ReactElement => {
    if (isPremiumUser) {
      return (
        <Button
          text={window.i18next.t('pmwjs_ai_credits_add_more')}
          type={Type.PRIMARY}
          size={Size.SMALL}
          customClasses="radius-8 flex-1"
          onClick={onAddCreditsClick}
          data-testid={AiChatTestId.ADD_MORE_CREDITS_CTA}
        />
      );
    }

    return (
      <Button
        text={window.i18next.t('pmwjs_ai_credits_upgrade_for_more')}
        type={Type.PREMIUM}
        size={Size.SMALL}
        customClasses="radius-8 flex-1"
        onClick={onUpgradeClick}
        data-testid={AiChatTestId.UPGRADE_FOR_CREDITS_CTA}
      />
    );
  };

  const getLearnMoreButton = (): ReactElement => {
    return <Text val={window.i18next.t('pmwjs_ai_credits_learn_how')} size={TextSize.XSMALL} bold className="content-link _cursor-pointer" onClick={onLearnMoreClick} />;
  };

  const getCreditsExpiryLines = (): string[] => {
    const lines: string[] = [];

    if (freeCredits > 0 && freeCreditsExpiryDate) {
      const formattedDate = formatExpiryDate(freeCreditsExpiryDate);
      if (formattedDate) {
        lines.push(window.i18next.t('pmwjs_x_plan_credits_expire_on_date', {numCredits: freeCredits.toLocaleString(), date: formattedDate}));
      }
    }

    if (legacyCredits > 0 && legacyCreditsExpiryDate) {
      const formattedDate = formatExpiryDate(legacyCreditsExpiryDate);
      if (formattedDate) {
        lines.push(window.i18next.t('pmwjs_x_legacy_credits_expire_on_date', {numCredits: legacyCredits.toLocaleString(), date: formattedDate}));
      }
    }

    if (purchasedCredits > 0) {
      lines.push(window.i18next.t('pmwjs_x_purchased_credits_never_expire', {numCredits: purchasedCredits.toLocaleString()}));
    }

    return lines;
  };

  const getCreditsExpiryList = (lines: string[], textClasses: string): ReactElement => {
    return (
      <ul className={styles.creditsExpiryList}>
        {lines.map((line) => (
          <li key={line}>
            <Text val={line} size={TextSize.MD} className={textClasses} />
          </li>
        ))}
      </ul>
    );
  };

  const getExpiryBreakdownTooltip = (lines: string[]): OverlayChildren => {
    return <Tooltip className=" _rounded-arrow">{getCreditsExpiryList(lines, TOOLTIP_TEXT_CLASSES)}</Tooltip>;
  };

  const openCreditsSummaryModal = (): void => {
    openMoreOptionsModal({
      headerTitle: window.i18next.t('pmwjs_ai_credits_summary'),
      content: <CreditsModalContent>{getCreditsDropdownPopup()}</CreditsModalContent>,
      panelId: CREDITS_MODAL_PANEL_ID,
      className: MODAL_PANEL_PADDING_CLASS,
      shouldCenterAlignTitle: true,
      showCloseButton: true,
    });
  };

  const getExpiryBreakdownModalContent = (): ReactElement => {
    return (
      <div className="flex-v-row gap-2">
        <Text val={window.i18next.t('pmwjs_ai_credits_breakdown_intro')} size={TextSize.MD} className={MODAL_TEXT_CLASSES} />
        {getCreditsExpiryList(getCreditsExpiryLines(), MODAL_TEXT_CLASSES)}
      </div>
    );
  };

  const openExpiryBreakdownModal = (): void => {
    openMoreOptionsModal({
      headerTitle: window.i18next.t('pmwjs_ai_credits_details'),
      content: <CreditsModalContent>{getExpiryBreakdownModalContent()}</CreditsModalContent>,
      panelId: CREDITS_MODAL_PANEL_ID,
      className: MODAL_PANEL_PADDING_CLASS,
      shouldCenterAlignTitle: true,
      showBackButton: true,
      showCloseButton: true,
      closeModalOnBack: false,
      onBack: openCreditsSummaryModal,
    });
  };

  const onExpiryAnchorClick = (): void => {
    if (!isMobile) {
      return;
    }

    openExpiryBreakdownModal();
  };

  const getExpiryBreakdown = (anchor: ReactElement): ReactElement | null => {
    const lines = getCreditsExpiryLines();

    if (lines.length === 0) {
      return null;
    }

    if (isMobile) {
      return anchor;
    }

    return (
      <OverlayTrigger trigger={['hover', 'focus']} placement="bottom" delay={{show: 0, hide: 0}} overlay={getExpiryBreakdownTooltip(lines)}>
        {anchor}
      </OverlayTrigger>
    );
  };

  const getRenewalText = (): string | null => {
    if (!renewalCredits || !renewalDate) {
      return null;
    }

    return getRenewalCreditsText(renewalCredits, renewalDate);
  };

  const getExpiryBreakdownIcon = (): ReactElement | null => {
    if (getRenewalText()) {
      return null;
    }

    const icon = (
      <div className={`flexbox ${styles.expiryBreakdownIcon}`}>
        <Icon icon="icon-info" size={IconSize.SIZE_ICON_16} isUsedAsButton ariaLabel={window.i18next.t('pmwjs_ai_credits_details')} onClick={onExpiryAnchorClick} />
      </div>
    );

    return getExpiryBreakdown(icon);
  };

  const getExpiryBreakdownLink = (): ReactElement | null => {
    const link = (
      <span className={styles.expiryBreakdownAnchor}>
        <Text val={window.i18next.t('pmwjs_learn_more')} size={TextSize.SM} className="ct-brand-primary-link _cursor-pointer" underline onClick={onExpiryAnchorClick} />
      </span>
    );

    return getExpiryBreakdown(link);
  };

  const getRenewalCreditsRow = (): ReactElement | null => {
    const renewalText = getRenewalText();

    if (!renewalText) {
      return null;
    }

    return (
      <div className={styles.creditsExpiryContainer}>
        <Divider additionalClasses="spacing-m-b-3"/>
        <div className={styles.renewalCreditsRow}>
          <Text val={renewalText} size={TextSize.SM} className="ct-neutral-secondary" />
          {getExpiryBreakdownLink()}
        </div>
      </div>
    );
  };

  const getCreditsDropdownPopup = (): ReactElement => {
    return (
      <div
        className={styles.creditsPopupContainer}
        onMouseEnter={cancelScheduledClose}
        onMouseLeave={scheduleClose}
      >
        <div className={styles.creditsInfoContainer}>{getCreditsRow()}</div>
        <div className={styles.creditsButtonsContainer}>
          {getCreditsActionButton()}
          {getLearnMoreButton()}
        </div>
        {getRenewalCreditsRow()}
      </div>
    );
  };

  const getCreditsPillSelector = (): ReactElement => {
    return (
      <div
        onMouseEnter={(): void => {
          cancelScheduledClose();
          setIsCreditsDropdownOpen(true);
        }}
        onMouseLeave={scheduleClose}
        data-testid={AiChatTestId.CREDITS_PILL}
      >
        <AiCreditsPill
          credits={state.credits}
          theme={CreditsPillTheme.DEFAULT}
          size={CreditsPillSize.MEDIUM}
          onClick={(): void => {
            setIsCreditsDropdownOpen(!isCreditsDropdownOpen);
          }}
        />
      </div>
    );
  };

  const getMobileCreditsPill = (): ReactElement => {
    // Wrapped rather than passing the id through AiCreditsPill: that component is shared with the poster
    // editor and stock media, and widening its props to carry a test id would reach surfaces this plan
    // does not cover. The desktop pill below shares this id — the two are breakpoint-exclusive.
    return (
      <div data-testid={AiChatTestId.CREDITS_PILL}>
        <AiCreditsPill credits={state.credits} theme={CreditsPillTheme.DEFAULT} size={CreditsPillSize.MEDIUM} onClick={openCreditsSummaryModal} />
      </div>
    );
  };

  const getCreditsPill = (): ReactElement | null => {
    if (!state.isUserLoggedIn) {
      return null;
    }

    if (!isPremiumUser && !state.isPaygUserWithAiChatAccess) {
      return null;
    }

    if (!isPremiumUser && state.chatHistory.length === 0 && !state.currentChatId) {
      return null;
    }

    if (isMobile) {
      return getMobileCreditsPill();
    }

    return (
      <Dropdown
        open={isCreditsDropdownOpen}
        className={isPremiumButtonNotNeeded() ? 'spacing-m-r-1' : ''}
        selector={getCreditsPillSelector()}
        popup={getCreditsDropdownPopup()}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        popUpHasCustomWidth
        popupClassName={styles.creditsDropdownPopup}
        onClose={(): void => {
          setIsCreditsDropdownOpen(false);
        }}
      />
    );
  };

  const showPurchasePremiumDialog = (): void => {
    window.PMW.openPremiumPricingModal(false);
  };

  const isPremiumButtonNotNeeded = (): boolean => {
    return state.isUserLoggedIn && isPremiumUser && !state.isSmall;
  };

  const getMobilePremiumButton = (): ReactElement => {
    return (
      <Button
        text={window.i18next.t('pmwjs_upgrade')}
        size={Size.XSMALL}
        type={Type.GHOST}
        textClasses={styles.mobileButtonTextColor}
        iconClassName={styles.mobileButtonTextColor}
        customClasses={'radius-8'}
        icon={'icon-crown'}
        onClick={showPurchasePremiumDialog}
      />
    );
  };
  const getPremiumButton = (): ReactElement | null => {
    if (!state.isUserLoggedIn) {
      return null;
    }

    if (isPremiumUser) {
      return null;
    }

    if (state.isSmall) {
      return getMobilePremiumButton();
    }

    return (
      <Button text={window.i18next.t('pmwjs_ai_credits_upgrade_for_more')} type={Type.PREMIUM} customClasses="radius-8" size={Size.SMALL} onClick={showPurchasePremiumDialog} />
    );
  };

  const getSuperUserOptions = (): ReactElement | null => {
    if (isMobile) {
      return null;
    }

    return <AiChatSuperUserOptions />;
  };

  if (state.isSmall) {
    return (
      <div className={'flex-row-align-center gap-2'}>
        {getLoginButton()}
        {getPremiumButton()}
        {getCreditsPill()}
        {getSuperUserOptions()}
      </div>
    );
  }

  return (
    <div className={'flex-row-align-center gap-2'}>
      {getLoginButton()}
      {getCreditsPill()}
      {getSuperUserOptions()}
      {getPremiumButton()}
    </div>
  );
}
